/**
 * The cave map being edited: one legend character per cell (docs/spec/03 §1). Grids are
 * immutable; every edit returns a new grid (or the same one when nothing changed), which keeps
 * undo simple. Pure.
 */

export interface Point {
  readonly x: number;
  readonly y: number;
}

export interface Grid {
  readonly w: number;
  readonly h: number;
  /** Row-major: cell (x, y) is `cells[y * w + x]`. */
  readonly cells: readonly string[];
}

export const MIN_W = 12;
export const MIN_H = 8;
export const MAX_W = 80;
export const MAX_H = 50;

/** A grid from map rows; short rows are padded with steel, as `buildCave` reads them. */
export function gridFromRows(rows: readonly string[]): Grid {
  const w = rows.reduce((n, r) => Math.max(n, r.length), 0);
  const cells: string[] = [];
  for (const row of rows) for (let x = 0; x < w; x++) cells.push(row[x] ?? 'W');
  return { w, h: rows.length, cells };
}

export function gridToRows(g: Grid): string[] {
  const rows: string[] = [];
  for (let y = 0; y < g.h; y++) rows.push(g.cells.slice(y * g.w, (y + 1) * g.w).join(''));
  return rows;
}

export function inGrid(g: Grid, x: number, y: number): boolean {
  return x >= 0 && y >= 0 && x < g.w && y < g.h;
}

export function cellAt(g: Grid, x: number, y: number): string | undefined {
  return inGrid(g, x, y) ? g.cells[y * g.w + x] : undefined;
}

export function isBorder(g: Grid, x: number, y: number): boolean {
  return x === 0 || y === 0 || x === g.w - 1 || y === g.h - 1;
}

/** A cave of dirt inside a steel border. */
export function blankGrid(w: number, h: number): Grid {
  const cells: string[] = [];
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) cells.push(x === 0 || y === 0 || x === w - 1 || y === h - 1 ? 'W' : '.');
  }
  return { w, h, cells };
}

/**
 * Paint `ch` on every point inside the grid. There is only ever one inbox: painting `P` clears any
 * other (to dirt) and only the last point of the stroke keeps it.
 */
export function paint(g: Grid, points: readonly Point[], ch: string): Grid {
  const inside = points.filter((p) => inGrid(g, p.x, p.y));
  if (inside.length === 0) return g;
  const cells = [...g.cells];
  let targets = inside;
  if (ch === 'P') {
    const last = inside[inside.length - 1] ?? { x: 0, y: 0 };
    targets = [last];
    for (let i = 0; i < cells.length; i++) if (cells[i] === 'P') cells[i] = '.';
  }
  for (const p of targets) cells[p.y * g.w + p.x] = ch;
  return cells.every((c, i) => c === g.cells[i]) ? g : { w: g.w, h: g.h, cells };
}

/**
 * The grid at a new size, keeping content where it was (anchored top-left). The border moves with
 * the edges and stays steel: an exit or inbox set into the old border moves with it, anything else
 * on the new border becomes steel, and new cells inside are dirt.
 */
export function resizeGrid(g: Grid, w: number, h: number): Grid {
  if (w === g.w && h === g.h) return g;
  // Where a new cell comes from: the far edges come from the old far edges, the rest from the same place.
  const fromX = (x: number): number => (x === w - 1 ? g.w - 1 : x < g.w - 1 ? x : -1);
  const fromY = (y: number): number => (y === h - 1 ? g.h - 1 : y < g.h - 1 ? y : -1);
  const cells: string[] = [];
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const ox = fromX(x);
      const oy = fromY(y);
      const old = ox >= 0 && oy >= 0 ? cellAt(g, ox, oy) : undefined;
      const border = x === 0 || y === 0 || x === w - 1 || y === h - 1;
      if (border) cells.push(old === 'X' || old === 'P' ? old : 'W');
      else cells.push(old ?? '.');
    }
  }
  return { w, h, cells };
}

export function countOf(g: Grid, ch: string): number {
  let n = 0;
  for (const c of g.cells) if (c === ch) n++;
  return n;
}
